import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, head } from 'lodash-es';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { VehicleModel } from 'client/data/models/vehicle-v2';
import { buildSubmodelsListPath, CoreResearchEntities, CoreResearchModel } from 'client/data/models/core-research';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/make/make';
import { filterSubmodels, getMakeBreadcrumbs, getMakeHistoryContentPath } from 'client/site-modules/make/utils/make';
import { TOP_MAKES_PATH } from 'site-modules/shared/constants/research';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { TopMakesSection } from 'site-modules/shared/components/top-makes-section/top-makes-section';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { ModelsView } from 'site-modules/make/components/models-view/models-view';
import { MakeHistory } from 'site-modules/make/components/make-history/make-history';
import { MakeNewsWidget } from 'site-modules/shared/components/news-widget/news-widget';
import { HeroImage } from 'site-modules/shared/components/hero-image/hero-image';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import convexMask from 'site-modules/shared/components/clipped-container/mask-library/convex-header';
import { MakeInfo } from 'site-modules/make/components/make-info/make-info';
import { PopularMakes } from 'site-modules/make/components/popular-makes/popular-makes';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';

import './make.scss';

function MakeUI({
  make = {},
  makeSlug,
  topMakesContent = DEFAULT_CONTENT,
  submodelsList = null,
  makeHeroImageContent = DEFAULT_CONTENT,
  isMobile = false,
  ads11412Recipe = 'ctrl',
}) {
  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const { pubStates = {}, name: makeName } = make;
  const breadcrumbsData = getMakeBreadcrumbs(makeName);

  const hasSubmodelsList = Boolean(submodelsList && submodelsList.length);

  const heroImage = head(makeHeroImageContent.links());
  const hasHeroImage = !!(heroImage && heroImage.href);

  const isAds11412Ctrl = ads11412Recipe === 'ctrl';

  return (
    <main id="main-content" tabIndex={-1} className="make-page pos-r">
      {isAds11412Ctrl ? (
        <Fragment>{hasHeroImage && <HeroImage heroImage={heroImage} title={makeName} />}</Fragment>
      ) : (
        <ClippedContainer containerClasses="curve-header pos-a top-0 left-0 w-100" mask={convexMask} />
      )}
      <Container>
        {isAds11412Ctrl && <Breadcrumbs data={breadcrumbsData} className="px-0 mb-2" />}
        {!isAds11412Ctrl && (
          <div className="bg-white rounded-12 px-2 py-2 hero-panel mb-3_5">
            <h1 className="heading-2">{makeName}</h1>
            <MakeInfo makeSlug={makeSlug} makeName={makeName} />
          </div>
        )}
        <Row>
          <Col xs={12} lg={8} className="pe-lg-1">
            {isAds11412Ctrl && (
              <Fragment>
                {!hasHeroImage && <h1 className="heading-2">{makeName}</h1>}
                <MakeHistory makeSlug={makeSlug} makeName={makeName} hasCollapse={hasSubmodelsList} />
              </Fragment>
            )}
            {hasSubmodelsList && (
              <ModelsView
                makeSlug={makeSlug}
                makeName={makeName}
                pubStates={pubStates}
                submodelsList={submodelsList}
                ads11412Recipe={ads11412Recipe}
              />
            )}
            <div className="py-3_5 py-lg-0 hidden-lg-up hidden-md-down">
              <MrectNative
                position={'2'}
                refreshable={false}
                wiredBreakpoints={{ md: true }}
                dfpCollapse
                useMinHeight
              />
            </div>
          </Col>
          <Col xs={12} lg={4} className="ps-lg-1">
            {isAds11412Ctrl ? (
              <Fragment>
                <hr className="mt-0 mt-lg-1_5 mb-2 hidden-lg-down" />
                <TopMakesSection
                  topMakes={topMakesContent}
                  className="px-1"
                  linkClassName="size-16"
                  linkTrackingId="make_index_view_makes"
                />
                <hr className="mt-0_5 mb-0 mb-lg-1_5" />
                <div className="py-3_5">
                  <MrectNative position={'1'} refreshable={false} dfpCollapse useMinHeight />
                </div>
                <hr className="mt-0 mb-2 hidden-lg-up" />
                <div className="py-lg-3_5 hidden-lg-down">
                  <MrectNative
                    position={'2'}
                    refreshable={false}
                    wiredBreakpoints={{ lg: true, xl: true }}
                    wiredOnly
                    dfpCollapse
                    addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                  />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <MrectNative position={'1'} refreshable={false} dfpCollapse useMinHeight wrapperClass="mb-1_5" />
                <MrectNative
                  position={'2'}
                  refreshable={false}
                  wiredBreakpoints={{ lg: true, xl: true }}
                  wiredOnly
                  dfpCollapse
                  addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                />
              </Fragment>
            )}
          </Col>
        </Row>
        <MakeNewsWidget
          makeName={makeName}
          makeSlug={makeSlug}
          withDivider={isAds11412Ctrl}
          className={classnames({ 'mt-2': !isAds11412Ctrl })}
        />
        {isAds11412Ctrl && <hr className="mt-1_5 mb-2 hidden-lg-down" />}
        {!isAds11412Ctrl && (
          <PopularMakes topMakes={topMakesContent} className="mt-3_5 mb-3_5" linkTrackingId="make_index_view_makes" />
        )}
        <PageLinksWidget divideSections={false} title="Related information" />
      </Container>
      {!isMobile && (
        <Experiment name="ads-11412" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal" />
        </Experiment>
      )}
    </main>
  );
}

MakeUI.propTypes = {
  makeSlug: PropTypes.string.isRequired,
  make: PropTypes.shape({}),
  topMakesContent: CmsEntities.Content,
  submodelsList: PropTypes.arrayOf(CoreResearchEntities.VehicleSubmodel),
  makeHeroImageContent: CmsEntities.Content,
  isMobile: PropTypes.bool,
  ads11412Recipe: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  ads11412Recipe: state.mobile
    ? 'ctrl'
    : ExperimentUtil.getForcedOrAssignedRecipeName({
        state,
        campaignName: 'ads-11412',
        defaultVal: 'ctrl',
      }),
});

const stateToPropsConfig = {
  make: bindToPath('makes', VehicleModel, (makes, { makeSlug }) => get(makes, makeSlug)),
  topMakesContent: bindToContent(TOP_MAKES_PATH, 'top-makes'),
  submodelsList: bindToPath(({ makeSlug }) => buildSubmodelsListPath(makeSlug), CoreResearchModel, filterSubmodels),
  makeHeroImageContent: bindToContent(({ makeSlug }) => getMakeHistoryContentPath(makeSlug), 'hero-content'),
};

export const Make = connect(mapStateToProps)(connectToModel(MakeUI, stateToPropsConfig));
